import React, { useEffect } from "react";
import { X, ZoomIn, ZoomOut, RotateCw } from "lucide-react";
import styles from "./ImageViewer.module.css";

interface Props {
    src: string;
    alt: string;
    isOpen: boolean;
    onClose: () => void;
}

export const ImageViewer: React.FC<Props> = ({ src, alt, isOpen, onClose }) => {
    const [zoom, setZoom] = React.useState(1);
    const [rotation, setRotation] = React.useState(0);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            document.body.style.overflow = "hidden";
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, onClose]);

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + 0.25, 3));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - 0.25, 0.5));
    };

    const handleRotate = () => {
        setRotation(prev => (prev + 90) % 360);
    };

    const handleReset = () => {
        setZoom(1);
        setRotation(0);
    };

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.container} onClick={e => e.stopPropagation()}>
                <div className={styles.header}>
                    <span className={styles.filename}>{alt}</span>
                    <div className={styles.controls}>
                        <button
                            className={styles.controlButton}
                            onClick={handleZoomOut}
                            aria-label="Zoom out"
                            title="Zoom out"
                        >
                            <ZoomOut size={20} />
                        </button>
                        <button
                            className={styles.controlButton}
                            onClick={handleZoomIn}
                            aria-label="Zoom in"
                            title="Zoom in"
                        >
                            <ZoomIn size={20} />
                        </button>
                        <button
                            className={styles.controlButton}
                            onClick={handleRotate}
                            aria-label="Rotate"
                            title="Rotate"
                        >
                            <RotateCw size={20} />
                        </button>
                        <button
                            className={styles.controlButton}
                            onClick={handleReset}
                            aria-label="Reset"
                            title="Reset"
                        >
                            Reset
                        </button>
                        <button
                            className={styles.closeButton}
                            onClick={onClose}
                            aria-label="Close"
                            title="Close (Esc)"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>
                <div className={styles.imageContainer}>
                    <img
                        src={src}
                        alt={alt}
                        className={styles.image}
                        style={{
                            transform: `scale(${zoom}) rotate(${rotation}deg)`,
                        }}
                        onClick={e => e.stopPropagation()}
                    />
                </div>
            </div>
        </div>
    );
};